import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Text } from '@cuemath/web-comps';
import { usePostMessage } from 'actions/support-agent';
import { isValidEmail, parseEmailList } from '../utils';
import { ERROR_AUTO_CLEAR_MS, FROM_EMAIL_OPTIONS } from '../constants';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnSecondary,
  BtnPrimary,
  IconBtn,
  ModalErrorMsg,
  EmailFieldsWrap,
  EmailFieldRow,
  EmailFieldLabel,
  EmailFieldInput,
  BccToggle,
  FilterDropdownWrap,
  FilterDropdownTrigger,
  FilterDropdownMenu,
  FilterDropdownItem,
  SA_COLORS,
} from '../styled-comps';
import Icon from './icon';

// ── Styled Components ─────────────────────────────────────────────────

const ModalEmailFieldsWrap = styled(EmailFieldsWrap)`
  gap: 4px;
`;

const FromDropdownWrap = styled(FilterDropdownWrap)`
  && {
    flex: 1;
  }
`;

const FromTrigger = styled(FilterDropdownTrigger)`
  border: none;
  padding: 2px 0;
  font-size: 13px;
`;

const FromMenu = styled(FilterDropdownMenu)`
  && {
    min-width: 100%;
  }
`;

const ChevIcon = styled(Icon)`
  transition: transform 0.15s ease;
  ${({ $open }) => $open && 'transform: rotate(180deg);'}
`;

const FromText = styled.span`
  color: ${({ $placeholder }) => ($placeholder ? SA_COLORS.TEXT_TERTIARY : 'inherit')};
`;

const ModalEmailFieldRow = styled(EmailFieldRow)`
  && {
    padding: 4px 0;
    border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};
  }
`;

const ComposerArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid ${SA_COLORS.BORDER_TERTIARY};
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: ${SA_COLORS.TEXT_PRIMARY};
  background: ${SA_COLORS.BG_PRIMARY};
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${SA_COLORS.INFO_600};
  }

  &::placeholder {
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

// ── Component ─────────────────────────────────────────────────────────

const EmailModal = ({ open, onClose, ticketId, onTicketUpdated }) => {
  const [fromEmail, setFromEmail] = useState('');
  const [fromOpen, setFromOpen] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showBcc, setShowBcc] = useState(false);
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const { post: postMessage } = usePostMessage({
    onComplete: err => {
      setSending(false);
      if (err != null) {
        setError(err || 'Failed to send email');
        setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      } else {
        if (onTicketUpdated) onTicketUpdated();
        handleClose();
      }
    },
  });

  const handleClose = useCallback(() => {
    setFromEmail('');
    setFromOpen(false);
    setToEmail('');
    setSubject('');
    setCc('');
    setBcc('');
    setShowBcc(false);
    setBody('');
    setError(null);
    setSending(false);
    onClose();
  }, [onClose]);

  const handleSend = useCallback(() => {
    if (!ticketId) {
      setError('No ticket selected');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!fromEmail) {
      setError('Please select a sender email');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!toEmail.trim()) {
      setError('Recipient email is required');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!body.trim()) {
      setError('Message body is required');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!isValidEmail(toEmail.trim())) {
      setError('Invalid recipient email address');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }

    const ccList = parseEmailList(cc);
    const bccList = parseEmailList(bcc);

    setSending(true);
    postMessage(
      {
        // Standalone outbound email (not a thread reply)
        type: 'EMAIL',
        content: body.trim(),
        message_type: 'TEXT',
        temp_id: crypto.randomUUID(),
        to_email: toEmail.trim(),
        from_email: fromEmail.trim() || undefined,
        email_subject: subject.trim() || undefined,
        email_cc: ccList.length ? ccList : undefined,
        email_bcc: bccList.length ? bccList : undefined,
      },
      ticketId,
    );
  }, [fromEmail, toEmail, subject, cc, bcc, body, ticketId, postMessage]);

  if (!open) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1">Send an Email</Text>
          <IconBtn onClick={handleClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>

        <ModalBody>
          <ModalEmailFieldsWrap>
            <ModalEmailFieldRow>
              <EmailFieldLabel>From</EmailFieldLabel>
              <FromDropdownWrap>
                <FromTrigger type="button" onClick={() => setFromOpen(p => !p)}>
                  <FromText $placeholder={!fromEmail}>
                    {fromEmail || 'Select sender email'}
                  </FromText>
                  <ChevIcon name="chev" size={14} $open={fromOpen} />
                </FromTrigger>
                {fromOpen && (
                  <FromMenu>
                    {FROM_EMAIL_OPTIONS.map(email => (
                      <FilterDropdownItem
                        key={email}
                        onClick={() => {
                          setFromEmail(email);
                          setFromOpen(false);
                        }}
                      >
                        {email}
                      </FilterDropdownItem>
                    ))}
                  </FromMenu>
                )}
              </FromDropdownWrap>
            </ModalEmailFieldRow>
            <ModalEmailFieldRow>
              <EmailFieldLabel>To</EmailFieldLabel>
              <EmailFieldInput
                value={toEmail}
                onChange={e => setToEmail(e.target.value)}
                placeholder="Recipient email address"
              />
            </ModalEmailFieldRow>
            <ModalEmailFieldRow>
              <EmailFieldLabel>CC</EmailFieldLabel>
              <EmailFieldInput
                value={cc}
                onChange={e => setCc(e.target.value)}
                placeholder="Comma-separated emails"
              />
              {!showBcc && <BccToggle onClick={() => setShowBcc(true)}>BCC</BccToggle>}
            </ModalEmailFieldRow>
            {showBcc && (
              <ModalEmailFieldRow>
                <EmailFieldLabel>BCC</EmailFieldLabel>
                <EmailFieldInput
                  value={bcc}
                  onChange={e => setBcc(e.target.value)}
                  placeholder="Comma-separated emails"
                />
              </ModalEmailFieldRow>
            )}
            <ModalEmailFieldRow>
              <EmailFieldLabel>Subject</EmailFieldLabel>
              <EmailFieldInput
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </ModalEmailFieldRow>
          </ModalEmailFieldsWrap>

          <ComposerArea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={5}
          />

          {error && <ModalErrorMsg>{error}</ModalErrorMsg>}
        </ModalBody>

        <ModalFoot>
          <BtnSecondary onClick={handleClose}>Cancel</BtnSecondary>
          <BtnPrimary onClick={handleSend} disabled={sending}>
            {sending ? 'Sending...' : 'Send Email'}
          </BtnPrimary>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

export default EmailModal;
